/**
 * The make page's fitment chart.
 *
 * The server draws one row per product, each labelled with the car it is for
 * (see `sections/airflux-collection.liquid` for why that is the baseline). This
 * folds those rows into the chart a reader actually wants:
 *
 *   1. Rows sharing a model and year range become ONE row with several options.
 *      387 of 534 model-generations stock exactly one thing, so most rows do not
 *      move — but an Audi A3 2004–2012 has four, and repeating "A3 2004–2012"
 *      four times reads as a list that failed to sort itself.
 *   2. Rows are ordered by model, then by year. Collection order is whatever the
 *      merchant last dragged it into, which is not an index.
 *   3. The field filters by model and year; the tabs filter by category.
 *
 * Everything comes from the DOM the server already rendered. There is no index
 * fetch and no second source of truth: if a price is wrong here it is wrong on
 * the product page too, which is the only way to keep the two honest.
 *
 * With scripting off the page is the same rows, unfolded and in collection
 * order — every model, every price and every link still present and crawlable.
 */
(async () => {
  'use strict';

  const chart = document.querySelector('[data-make-chart]');
  if (!chart) return;

  const scope = document.querySelector('[data-make-scope]');
  const field = document.querySelector('[data-make-filter]');
  const empty = document.querySelector('[data-make-empty]');
  const emptyText = document.querySelector('[data-make-empty-text]');
  const tabs = [...document.querySelectorAll('[data-make-tab]')];
  const clears = [...document.querySelectorAll('[data-make-clear]')];

  /**
   * Complete the list before anything is folded or counted.
   *
   * Shopify caps a paginated loop at 50 products and five makes are over it —
   * Ford has 66. Page one on its own would make every number on this page a
   * page-local half-truth ("15 Ford models covered" on page two, when the make
   * has 47) and would break the filter in the way a reader notices first: type
   * "Focus" and be told Ford does not make one, because it is on page two.
   *
   * So the remaining pages are fetched and their rows merged in. If a fetch
   * fails the pager is left exactly where it is and the page carries on as the
   * server drew it — a partial list with a visible way to the rest is a fair
   * fallback; a partial list pretending to be whole is not.
   */
  async function mergePages() {
    const pages = Number(chart.dataset.pages || 1);
    if (pages < 2) return true;

    const here = Number(chart.dataset.page || 1);
    const wanted = [];
    for (let p = 1; p <= pages; p += 1) if (p !== here) wanted.push(p);

    // Two at a time. Every one of these is a full collection render on the
    // server, and the biggest make is also the slowest page to build — firing
    // all of them at once turns one visitor into a self-inflicted load spike on
    // exactly the page that can least afford it. Ford is one extra request
    // today; the cap is what keeps that true if a make ever triples.
    const LANES = 2;

    try {
      const queue = [...wanted];
      const collected = [];
      const lane = async () => {
        for (let p = queue.shift(); p !== undefined; p = queue.shift()) {
          // Built per request: one shared URL object mutated inside concurrent
          // lanes is a race waiting to happen, even where it reads correctly
          // today.
          const url = new URL(location.href);
          url.searchParams.set('page', p);
          const res = await fetch(url.toString(), { headers: { Accept: 'text/html' } });
          if (!res.ok) throw new Error(`page ${p}: ${res.status}`);
          collected.push([p, await res.text()]);
        }
      };
      await Promise.all(Array.from({ length: Math.min(LANES, queue.length) }, lane));

      // Page order, not completion order — the fold sorts the rows afterwards,
      // but a stable starting order is what keeps the dedupe's "whichever came
      // first" tie-break stable between loads.
      collected.sort((a, b) => a[0] - b[0]);
      for (const [, html] of collected) {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        for (const row of doc.querySelectorAll('[data-make-row]')) chart.appendChild(row);
      }
      return true;
    } catch {
      return false;
    }
  }

  const complete = await mergePages();

  // Only once the list is whole. Left in place otherwise, because it is then
  // still the only route to the rest of the make.
  if (complete) document.querySelector('.af-make__pagination')?.remove();

  /**
   * Fold rows that share a car into one.
   *
   * The first row for a car keeps its `.af-make__car` and adopts the options
   * from the rest; the emptied rows are removed. Matching is on `data-key`,
   * which the server built from the model and the year range — the same pair a
   * reader matches their own car against.
   */
  const rows = [...chart.querySelectorAll('[data-make-row]')];
  const byKey = new Map();

  for (const row of rows) {
    const key = row.dataset.key;
    const first = byKey.get(key);
    if (!first) {
      byKey.set(key, row);
      continue;
    }
    const into = first.querySelector('.af-make__opts');
    for (const opt of row.querySelectorAll('.af-make__opt')) into.appendChild(opt);
    // The categories now on this row, so the tabs can hide a row that has
    // nothing in the category being shown.
    first.dataset.cat = [...new Set([...first.dataset.cat.split(' '), ...row.dataset.cat.split(' ')])]
      .sort()
      .join(' ');
    row.remove();
  }

  /**
   * Model, then year. `localeCompare` with `numeric` so "A3" sorts before "A10"
   * and "Series 3" before "Series 21" — a plain string sort puts A10 first,
   * which is the kind of thing a reader reads as a broken list rather than as a
   * different sort order.
   */
  const folded = [...byKey.values()].sort(
    (a, b) =>
      a.dataset.model.localeCompare(b.dataset.model, undefined, { numeric: true, sensitivity: 'base' }) ||
      a.dataset.years.localeCompare(b.dataset.years, undefined, { numeric: true })
  );
  for (const row of folded) chart.appendChild(row);
  chart.setAttribute('data-folded', '');

  /**
   * Collapse duplicate listings, then order what survives.
   *
   * The catalogue contains genuine duplicates — the same item entered twice,
   * sometimes at prices two pence apart — and a row showing both reads as a
   * pricing error rather than as honesty (§3.2a). The survivor is the NEWEST
   * upload day (revised 2026-09-05), then in stock, then cheapest, then
   * whichever the collection listed first, which is stable. Identical to the
   * rule the widget and the PDP cross-sell apply, so a customer is never shown
   * three different answers to "which one is it".
   *
   * `data-uploaded` is YYYYMMDD, so a missing attribute reads as 0 and sorts
   * oldest — an unknown date must not win a contest it cannot evidence.
   *
   * ⚠️ The key includes BODY, so an A3's 3-door set and its 5-door set are two
   * things to buy and both stay. Collapsing on channel and pack alone would
   * hide one of them — and hide it invisibly, which is the failure mode §3.2a
   * warns about: nobody sees an absence.
   */
  for (const row of folded) {
    const box = row.querySelector('.af-make__opts');
    const best = new Map();

    const up = (el) => Number(el.dataset.uploaded || 0);
    const av = (el) => el.hasAttribute('data-available');

    for (const opt of [...box.children]) {
      const key = opt.dataset.dedupe;
      const held = best.get(key);
      if (!held) {
        best.set(key, opt);
        continue;
      }
      const better =
        up(opt) > up(held) ||
        (up(opt) === up(held) &&
          ((av(opt) && !av(held)) ||
            (av(opt) === av(held) && Number(opt.dataset.price) < Number(held.dataset.price))));
      if (better) {
        best.set(key, opt);
        held.remove();
      } else {
        opt.remove();
      }
    }

    // Deflectors before wipers, then cheapest first — the same order the buy
    // column reads in, so the eye lands on the same thing twice.
    const kept = [...best.values()].sort(
      (a, b) =>
        (a.dataset.cat || '').localeCompare(b.dataset.cat || '') || Number(a.dataset.price) - Number(b.dataset.price)
    );
    for (const opt of kept) box.appendChild(opt);

    // The row's categories are recomputed from what survived: a row whose only
    // wiper lost a dedupe would otherwise still claim one to the tabs.
    row.dataset.cat = [...new Set(kept.map((o) => o.dataset.cat))].sort().join(' ');
  }

  /** How many models each tab would show. Counted once — nothing here changes. */
  const total = folded.length;
  const counts = {
    all: total,
    d: folded.filter((r) => r.dataset.cat.includes('d')).length,
    w: folded.filter((r) => r.dataset.cat.includes('w')).length,
  };
  for (const [key, n] of Object.entries(counts)) {
    const out = document.querySelector(`[data-count-${key}]`);
    if (out) out.textContent = n;
  }

  // The scope line starts as a part count, because that is all the server can
  // count cheaply. Now that the rows are folded, the model count is known — and
  // that is the number a reader is actually deciding against.
  // Only when the list is whole. On a make whose extra pages failed to load,
  // `total` counts the rows on screen, and "15 Ford models covered" is a claim
  // about Ford — not about this page. The server's part count stays instead: it
  // is smaller-sounding and completely true.
  if (complete && scope?.dataset.models) {
    scope.textContent =
      total === 1 && scope.dataset.modelsOne ? scope.dataset.modelsOne : scope.dataset.models.replace('%n%', total);
  }

  let query = '';
  let category = 'all';

  function apply() {
    const q = query.trim().toLowerCase();
    let shown = 0;

    for (const row of folded) {
      const matchesText = !q || `${row.dataset.model} ${row.dataset.years}`.toLowerCase().includes(q);
      const matchesCat = category === 'all' || row.dataset.cat.includes(category);
      const show = matchesText && matchesCat;
      row.hidden = !show;
      if (show) shown += 1;

      // Inside a shown row, dim nothing — hide the options that are not in the
      // category being asked about. A "Wiper blades" tab that still lists the
      // deflector beside it has not answered the question.
      if (show && category !== 'all') {
        for (const opt of row.querySelectorAll('.af-make__opt')) {
          opt.hidden = opt.dataset.cat !== category;
        }
      } else {
        for (const opt of row.querySelectorAll('.af-make__opt')) opt.hidden = false;
      }
    }

    if (empty) {
      empty.hidden = shown > 0;
      if (!shown && emptyText) {
        emptyText.textContent = (empty.dataset.template || '').replace('%q%', query.trim());
      }
    }
    for (const btn of clears) btn.hidden = !query;
  }

  field?.addEventListener('input', () => {
    query = field.value;
    apply();
  });

  for (const btn of clears) {
    btn.addEventListener('click', () => {
      query = '';
      if (field) field.value = '';
      apply();
      field?.focus();
    });
  }

  /**
   * A roving tabindex and arrow keys, matching the plate/manual tablist in the
   * fitment widget — the two are the same control in two places, so they behave
   * the same way for anyone driving them from the keyboard.
   */
  function select(tab) {
    for (const t of tabs) {
      const on = t === tab;
      t.setAttribute('aria-selected', String(on));
      t.tabIndex = on ? 0 : -1;
    }
    category = tab.dataset.makeTab;
    apply();
  }

  for (const tab of tabs) {
    tab.addEventListener('click', () => select(tab));
    tab.addEventListener('keydown', (e) => {
      const step = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
      if (!step) return;
      e.preventDefault();
      const next = tabs[(tabs.indexOf(tab) + step + tabs.length) % tabs.length];
      next.focus();
      select(next);
    });
  }

  apply();
})();
